import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    Controls,
    Panel,
    addEdge,
    useNodesState,
    useEdgesState,
    useReactFlow,
    Connection,
    Edge,
    Node,
    MarkerType,
} from '@xyflow/react';
import ELK from 'elkjs';
import '@xyflow/react/dist/style.css';
import { toast } from 'sonner';
import {
    Copy,
    ClipboardPaste,
    Download,
    FileUp,
    History,
    Image as ImageIcon,
    ListChecks,
    Loader2,
    Play,
    Plus,
    QrCode,
    Save,
    Share2,
    Trash2,
    UploadCloud,
    Zap,
} from 'lucide-react';

import useWorkflow, { WorkflowSummary } from './useWorkflow';
import { knownNodeTypes, GenericNode, RFNodeData } from './WorkflowNodes';
import NodeConfigPanel from './NodeConfigPanel';
import VersionHistoryPanel from './VersionHistoryPanel';
import RunHistoryPanel from './RunHistoryPanel';
import { suggestionsForNode } from './variables';
import {
    WorkflowDefinition,
    WorkflowNode,
    WorkflowNodeConfig,
    WorkflowTriggerType,
    TestRunResult,
    WorkflowCatalog,
} from './types';

const VALID_TRIGGER_TYPES: WorkflowTriggerType[] = [
    'qrScan',
    'event',
    'share',
    'mediaUploaded',
];

/** Tab icon + hover title for a non-qrScan (multi-instance) workflow. */
function triggerTabMeta(
    triggerType: WorkflowTriggerType,
    eventName: string | null,
): { icon: JSX.Element; title: string } {
    switch (triggerType) {
        case 'share':
            return { icon: <Share2 size={13} />, title: 'On share created' };
        case 'mediaUploaded':
            return {
                icon: <ImageIcon size={13} />,
                title: 'On media uploaded',
            };
        case 'event':
        default:
            return {
                icon: <Zap size={13} />,
                title: `On event "${eventName ?? ''}"`,
            };
    }
}

const elk = new ELK();

let idCounter = 0;
function nextId(type: string) {
    idCounter += 1;
    return `${type}_${Date.now().toString(36)}${idCounter}`;
}

// Module-level clipboard so copied steps survive re-renders and can be pasted
// into a different activation's workflow within the same session.
let stepClipboard: { nodes: Node[]; edges: Edge[] } | null = null;

function uniqueKey(base: string, existing: Set<string>): string {
    let candidate = `${base}_copy`;
    let n = 2;
    while (existing.has(candidate)) {
        candidate = `${base}_copy${n}`;
        n += 1;
    }
    return candidate;
}

function defaultConfig(type: string): WorkflowNodeConfig {
    switch (type) {
        case 'trigger':
            return { parseJson: true };
        case 'transform':
            return { assignments: [] };
        case 'httpRequest':
            return { method: 'GET', url: '', headers: [], bodyMode: 'none' };
        case 'lookupMedia':
            return { shareIdentifiers: '' };
        case 'branch':
            return { conditions: [] };
        case 'emitCodes':
            return { codes: [] };
        default:
            // Unknown / future step type registered on the backend.
            return {} as WorkflowNodeConfig;
    }
}

function toRFNodes(definition: WorkflowDefinition): Node[] {
    return (definition.nodes ?? []).map((n, index) => ({
        id: n.id,
        type: n.type,
        position: n.position ?? { x: 60 + index * 240, y: 80 },
        data: {
            nodeType: n.type,
            nodeKey: n.key && n.key.length > 0 ? n.key : n.id,
            label: n.label,
            config: n.data,
        } as RFNodeData,
    }));
}

function toRFEdges(definition: WorkflowDefinition): Edge[] {
    return (definition.edges ?? []).map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        markerEnd: { type: MarkerType.ArrowClosed },
    }));
}

function toDefinition(
    nodes: Node[],
    edges: Edge[],
    responseType: string,
): WorkflowDefinition {
    return {
        responseType,
        nodes: nodes.map((n) => {
            const data = n.data as RFNodeData;
            const node: WorkflowNode = {
                id: n.id,
                type: data.nodeType,
                key: data.nodeKey,
                label: data.label,
                position: n.position,
                data: data.config,
            };
            return node;
        }),
        edges: edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle ?? undefined,
        })),
    };
}

interface WorkflowBuilderProps {
    activationId: number;
}

function Inner({ activationId }: WorkflowBuilderProps) {
    const {
        getWorkflow,
        saveWorkflow,
        testRun,
        publishWorkflow,
        getCatalog,
        listWorkflows,
        createWorkflow,
        deleteWorkflow,
        importWorkflow,
    } = useWorkflow();
    const { getNodes, setNodes: rfSetNodes, getEdges, fitView } = useReactFlow();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [catalog, setCatalog] = useState<WorkflowCatalog>({
        triggers: [{ type: 'qrScan', label: 'QR scan' }],
        steps: [
            { type: 'transform', label: 'Transform', category: 'action' },
            { type: 'httpRequest', label: 'HTTP request', category: 'action' },
            { type: 'branch', label: 'Branch', category: 'logic' },
            {
                type: 'emitCodes',
                label: 'Emit clickerbooth codes',
                category: 'output',
            },
        ],
        responses: [
            { type: 'clickerboothCodes', label: 'Clickerbooth codes' },
        ],
        defaultTriggerType: 'qrScan',
        defaultResponseType: 'clickerboothCodes',
    });
    const [responseType, setResponseType] = useState('clickerboothCodes');

    const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showRuns, setShowRuns] = useState(false);
    const [publishedVersionNumber, setPublishedVersionNumber] = useState<
        number | null
    >(null);
    const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
    const [testInput, setTestInput] = useState(
        '{\n  "ticketId": "T-123",\n  "email": "guest@example.com",\n  "tier": "vip"\n}',
    );
    const [testResult, setTestResult] = useState<TestRunResult | null>(null);
    const [isTesting, setIsTesting] = useState(false);

    // The activation's workflows: one qrScan workflow (edited through the
    // legacy singular routes when selectedWorkflowId is null) plus any number
    // of event-triggered workflows (always edited by id).
    const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(
        null,
    );

    const refreshWorkflows = useCallback(async () => {
        try {
            const list = await listWorkflows(activationId);
            setWorkflows(list);
        } catch {
            // The list is a navigation nicety; the editor still works without it.
        }
    }, [activationId, listWorkflows]);

    useEffect(() => {
        refreshWorkflows();
    }, [refreshWorkflows]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setIsLoading(true);
            try {
                const saved = await getWorkflow(activationId, selectedWorkflowId);
                if (cancelled) return;
                let definition = saved.definition;
                if (!definition.nodes || definition.nodes.length === 0) {
                    // Seed with a trigger so the canvas is never empty.
                    definition = {
                        nodes: [
                            {
                                id: nextId('trigger'),
                                type: 'trigger',
                                key: 'trigger',
                                label: 'Scan',
                                position: { x: 80, y: 120 },
                                data: defaultConfig('trigger'),
                            },
                        ],
                        edges: [],
                    };
                }
                setNodes(toRFNodes(definition));
                setEdges(toRFEdges(definition));
                setEnabled(saved.enabled);
                setResponseType(definition.responseType || 'clickerboothCodes');
                setPublishedVersionNumber(saved.publishedVersionNumber);
                setHasUnpublishedChanges(saved.hasUnpublishedChanges);
            } catch (err) {
                console.error('Failed to load workflow', err);
                toast.error('Failed to load workflow');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId, selectedWorkflowId]);

    // Discover available triggers / steps / responses from the backend so new
    // registry entries surface in the editor without a code change here.
    useEffect(() => {
        let cancelled = false;
        getCatalog()
            .then((c) => {
                if (!cancelled) setCatalog(c);
            })
            .catch(() => {
                /* keep the built-in defaults */
            });
        return () => {
            cancelled = true;
        };
    }, [getCatalog]);

    // Map every catalog step type to a renderer: known ones get their custom
    // component, the rest fall back to the generic node.
    const nodeTypes = useMemo(() => {
        const map: Record<string, any> = { ...knownNodeTypes };
        catalog.steps.forEach((s) => {
            if (!map[s.type]) map[s.type] = GenericNode;
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalog]);

    const onConnect = useCallback(
        (connection: Connection) => {
            setEdges((eds) =>
                addEdge(
                    {
                        ...connection,
                        id: nextId('edge'),
                        markerEnd: { type: MarkerType.ArrowClosed },
                    },
                    eds,
                ),
            );
        },
        [setEdges],
    );

    const addNode = (type: string) => {
        const id = nextId(type);
        const newNode: Node = {
            id,
            type,
            position: {
                x: 120 + Math.random() * 240,
                y: 240 + Math.random() * 120,
            },
            data: {
                nodeType: type,
                nodeKey: type,
                label: undefined,
                config: defaultConfig(type),
            } as RFNodeData,
        };
        setNodes((nds) => [...nds, newNode]);
        setSelectedId(id);
    };

    const selectedNode = useMemo(
        () => nodes.find((n) => n.id === selectedId) ?? null,
        [nodes, selectedId],
    );

    const definition = useMemo(
        () => toDefinition(nodes, edges, responseType),
        [nodes, edges, responseType],
    );

    const suggestions = useMemo(() => {
        if (!selectedId) return [];
        return suggestionsForNode(
            selectedId,
            definition.nodes,
            definition.edges,
        );
    }, [selectedId, definition]);

    const updateSelectedConfig = (config: WorkflowNodeConfig) => {
        setNodes((nds) =>
            nds.map((n) =>
                n.id === selectedId
                    ? { ...n, data: { ...(n.data as RFNodeData), config } }
                    : n,
            ),
        );
    };

    const updateSelectedMeta = (patch: { key?: string; label?: string }) => {
        setNodes((nds) =>
            nds.map((n) => {
                if (n.id !== selectedId) return n;
                const data = n.data as RFNodeData;
                return {
                    ...n,
                    data: {
                        ...data,
                        nodeKey: patch.key ?? data.nodeKey,
                        label: patch.label ?? data.label,
                    },
                };
            }),
        );
    };

    const deleteSelected = () => {
        if (!selectedId) return;
        setNodes((nds) => nds.filter((n) => n.id !== selectedId));
        setEdges((eds) =>
            eds.filter(
                (e) => e.source !== selectedId && e.target !== selectedId,
            ),
        );
        setSelectedId(null);
    };

    const autoLayout = useCallback(() => {
        const graph = {
            id: 'root',
            layoutOptions: {
                'elk.algorithm': 'layered',
                'elk.direction': 'RIGHT',
                'elk.layered.spacing.nodeNodeBetweenLayers': '120',
                'elk.spacing.nodeNode': '80',
            },
            children: getNodes().map((node) => ({
                id: node.id,
                width: node.measured?.width ?? 200,
                height: node.measured?.height ?? 80,
            })),
            edges: getEdges().map((e) => ({
                id: e.id,
                sources: [e.source],
                targets: [e.target],
            })),
        };
        // @ts-ignore - elk's loose typing
        elk.layout(graph).then(({ children }) => {
            const positions = new Map<string, { x: number; y: number }>();
            // @ts-ignore
            children.forEach((c) => positions.set(c.id, { x: c.x, y: c.y }));
            rfSetNodes((nds) =>
                nds.map((n) => {
                    const pos = positions.get(n.id);
                    return pos ? { ...n, position: pos } : n;
                }),
            );
            window.requestAnimationFrame(() => fitView());
        });
    }, [getNodes, getEdges, rfSetNodes, fitView]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await saveWorkflow(
                activationId,
                definition,
                enabled,
                selectedWorkflowId,
            );
            // The draft now (potentially) differs from the published version.
            setHasUnpublishedChanges(true);
            toast.success('Workflow saved');
            // A first legacy save creates the qrScan workflow row.
            refreshWorkflows();
        } catch (err: any) {
            const details = err.response?.data?.details;
            toast.error(
                details
                    ? `Invalid workflow: ${details.join('; ')}`
                    : 'Failed to save workflow',
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublish = async () => {
        setIsPublishing(true);
        try {
            // Save the latest draft first so we publish exactly what's on screen.
            await saveWorkflow(
                activationId,
                definition,
                enabled,
                selectedWorkflowId,
            );
            const { publishedVersionNumber: num } = await publishWorkflow(
                activationId,
                undefined,
                selectedWorkflowId,
            );
            setPublishedVersionNumber(num);
            setHasUnpublishedChanges(false);
            toast.success(`Published version ${num}`);
            refreshWorkflows();
        } catch (err: any) {
            const details = err.response?.data?.details;
            toast.error(
                details
                    ? `Cannot publish: ${details.join('; ')}`
                    : err.response?.data?.error ?? 'Failed to publish workflow',
            );
        } finally {
            setIsPublishing(false);
        }
    };

    const handleRestored = async () => {
        setShowHistory(false);
        // Reload the draft (now holding the restored definition) and flags.
        const saved = await getWorkflow(activationId, selectedWorkflowId);
        setNodes(toRFNodes(saved.definition));
        setEdges(toRFEdges(saved.definition));
        setEnabled(saved.enabled);
        setPublishedVersionNumber(saved.publishedVersionNumber);
        setHasUnpublishedChanges(saved.hasUnpublishedChanges);
        setSelectedId(null);
        toast.success('Restored version into draft');
    };

    const TRIGGER_LABELS: Record<string, string> = {
        event: 'Event',
        share: 'Share',
        mediaUploaded: 'Media uploaded',
    };

    const handleAddTriggerWorkflow = async (
        triggerType: WorkflowTriggerType,
    ) => {
        const typeLabel = TRIGGER_LABELS[triggerType] ?? 'Workflow';
        const name = window.prompt(`${typeLabel} workflow name`);
        if (!name) return;
        let eventName: string | undefined;
        if (triggerType === 'event') {
            const enteredEvent = window.prompt(
                'Event name the booth emits (e.g. "teamPhotoComplete")',
            );
            if (!enteredEvent) return;
            eventName = enteredEvent.trim();
        }
        try {
            const created = await createWorkflow(activationId, {
                name: name.trim(),
                triggerType,
                eventName,
            });
            await refreshWorkflows();
            setSelectedWorkflowId(created.id);
            toast.success(`${typeLabel} workflow created`);
        } catch (err: any) {
            toast.error(
                err.response?.data?.error ??
                    `Failed to create ${typeLabel.toLowerCase()} workflow`,
            );
        }
    };

    const handleDeleteWorkflow = async (workflow: WorkflowSummary) => {
        if (
            !window.confirm(
                `Delete "${workflow.name ?? 'workflow'}" and its version history?`,
            )
        ) {
            return;
        }
        try {
            await deleteWorkflow(activationId, workflow.id);
            if (selectedWorkflowId === workflow.id) {
                setSelectedWorkflowId(null);
            }
            await refreshWorkflows();
            toast.success('Workflow deleted');
        } catch (err: any) {
            toast.error(err.response?.data?.error ?? 'Failed to delete workflow');
        }
    };

    // --- Import / export ---

    // Reload a workflow's saved draft into the canvas. Used after an import
    // when the selected slot doesn't change (re-importing the qrScan workflow),
    // since the load effect only re-runs when selectedWorkflowId changes.
    const reloadWorkflow = useCallback(
        async (workflowId: number | null) => {
            const saved = await getWorkflow(activationId, workflowId);
            setNodes(toRFNodes(saved.definition));
            setEdges(toRFEdges(saved.definition));
            setEnabled(saved.enabled);
            setResponseType(
                saved.definition.responseType || 'clickerboothCodes',
            );
            setPublishedVersionNumber(saved.publishedVersionNumber);
            setHasUnpublishedChanges(saved.hasUnpublishedChanges);
            setSelectedId(null);
        },
        [activationId, getWorkflow, setNodes, setEdges],
    );

    const handleExport = useCallback(() => {
        const summary =
            selectedWorkflowId !== null
                ? workflows.find((w) => w.id === selectedWorkflowId)
                : workflows.find((w) => w.triggerType === 'qrScan');
        const triggerType: WorkflowTriggerType =
            summary?.triggerType ?? 'qrScan';
        const name =
            summary?.name ??
            (triggerType === 'qrScan' ? 'QR scan workflow' : 'Workflow');

        // Keep the field order matching the documented export format.
        const exported: Record<string, unknown> = { name, triggerType };
        if (triggerType === 'event' && summary?.eventName) {
            exported.eventName = summary.eventName;
        }
        exported.enabled = enabled;
        exported.definition = definition;

        const blob = new Blob([JSON.stringify(exported, null, 2)], {
            type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const slug =
            name
                .replace(/[^a-z0-9]+/gi, '-')
                .toLowerCase()
                .replace(/^-+|-+$/g, '') || 'workflow';
        a.download = `${slug}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }, [selectedWorkflowId, workflows, enabled, definition]);

    const handleImportFile = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = e.target.files?.[0];
        // Reset so picking the same file again re-fires the change event.
        e.target.value = '';
        if (!file) return;

        let parsed: any;
        try {
            parsed = JSON.parse(await file.text());
        } catch {
            toast.error('Invalid JSON file');
            return;
        }
        if (!parsed || typeof parsed !== 'object' || !parsed.definition) {
            toast.error('Invalid workflow file: missing "definition"');
            return;
        }

        const triggerType: WorkflowTriggerType = VALID_TRIGGER_TYPES.includes(
            parsed.triggerType,
        )
            ? parsed.triggerType
            : 'qrScan';

        try {
            const created = await importWorkflow(activationId, {
                name: parsed.name,
                triggerType,
                eventName: parsed.eventName,
                enabled: !!parsed.enabled,
                definition: parsed.definition,
            });
            await refreshWorkflows();
            if (triggerType !== 'qrScan') {
                // Multi-instance workflow: selecting the new id triggers the load effect.
                setSelectedWorkflowId(created.id);
            } else if (selectedWorkflowId === null) {
                // Already on the scan slot: the effect won't re-run, so reload.
                await reloadWorkflow(null);
            } else {
                setSelectedWorkflowId(null);
            }
            toast.success(`Imported "${created.name ?? 'workflow'}"`);
        } catch (err: any) {
            const details = err.response?.data?.details;
            toast.error(
                details
                    ? `Import failed: ${details.join('; ')}`
                    : err.response?.data?.error ?? 'Failed to import workflow',
            );
        }
    };

    // --- Copy / paste / duplicate of steps ---

    const cloneNodes = useCallback(
        (source: Node[], allExistingKeys: Set<string>) => {
            const idMap = new Map<string, string>();
            const usedKeys = new Set(allExistingKeys);
            const cloned = source.map((n) => {
                const data = n.data as RFNodeData;
                const newId = nextId(data.nodeType);
                idMap.set(n.id, newId);
                const newKey = uniqueKey(data.nodeKey, usedKeys);
                usedKeys.add(newKey);
                return {
                    ...n,
                    id: newId,
                    selected: true,
                    position: {
                        x: n.position.x + 48,
                        y: n.position.y + 48,
                    },
                    data: {
                        ...data,
                        nodeKey: newKey,
                        // Deep-copy config so edits don't mutate the original.
                        config: JSON.parse(JSON.stringify(data.config)),
                    },
                } as Node;
            });
            return { cloned, idMap };
        },
        [],
    );

    const pasteFromClipboard = useCallback(() => {
        if (!stepClipboard || stepClipboard.nodes.length === 0) return;
        const existingKeys = new Set(
            getNodes().map((n) => (n.data as RFNodeData).nodeKey),
        );
        const { cloned, idMap } = cloneNodes(stepClipboard.nodes, existingKeys);
        const clonedEdges = stepClipboard.edges
            .filter((e) => idMap.has(e.source) && idMap.has(e.target))
            .map((e) => ({
                ...e,
                id: nextId('edge'),
                source: idMap.get(e.source) as string,
                target: idMap.get(e.target) as string,
                markerEnd: { type: MarkerType.ArrowClosed },
            }));

        setNodes((nds) => [
            ...nds.map((n) => ({ ...n, selected: false })),
            ...cloned,
        ]);
        setEdges((eds) => [...eds, ...clonedEdges]);
        toast.success(
            `Pasted ${cloned.length} step${cloned.length === 1 ? '' : 's'}`,
        );
    }, [cloneNodes, getNodes, setNodes, setEdges]);

    const copySelection = useCallback(() => {
        const selected = getNodes().filter((n) => n.selected);
        if (selected.length === 0) return;
        const selectedIds = new Set(selected.map((n) => n.id));
        const internalEdges = getEdges().filter(
            (e) => selectedIds.has(e.source) && selectedIds.has(e.target),
        );
        stepClipboard = {
            nodes: JSON.parse(JSON.stringify(selected)),
            edges: JSON.parse(JSON.stringify(internalEdges)),
        };
        toast.success(
            `Copied ${selected.length} step${
                selected.length === 1 ? '' : 's'
            }`,
        );
    }, [getNodes, getEdges]);

    const duplicateNode = useCallback(
        (nodeId: string) => {
            const node = getNodes().find((n) => n.id === nodeId);
            if (!node) return;
            const existingKeys = new Set(
                getNodes().map((n) => (n.data as RFNodeData).nodeKey),
            );
            const { cloned } = cloneNodes([node], existingKeys);
            setNodes((nds) => [
                ...nds.map((n) => ({ ...n, selected: false })),
                ...cloned,
            ]);
            setSelectedId(cloned[0].id);
        },
        [cloneNodes, getNodes, setNodes],
    );

    // Keyboard shortcuts: Cmd/Ctrl + C / V / D. Ignored while typing in a field.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (!(e.metaKey || e.ctrlKey)) return;
            const target = e.target as HTMLElement | null;
            const tag = target?.tagName;
            if (
                tag === 'INPUT' ||
                tag === 'TEXTAREA' ||
                tag === 'SELECT' ||
                target?.isContentEditable
            ) {
                return;
            }
            const key = e.key.toLowerCase();
            if (key === 'c') {
                copySelection();
            } else if (key === 'v') {
                pasteFromClipboard();
            } else if (key === 'd') {
                e.preventDefault();
                if (selectedId) duplicateNode(selectedId);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [copySelection, pasteFromClipboard, duplicateNode, selectedId]);

    const handleTest = async () => {
        setIsTesting(true);
        setTestResult(null);
        try {
            const result = await testRun(activationId, definition, testInput);
            setTestResult(result);
        } finally {
            setIsTesting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-64 items-center justify-center text-gray-500">
                <Loader2 className="animate-spin" /> Loading workflow…
            </div>
        );
    }

    const triggeredWorkflows = workflows.filter(
        (w) => w.triggerType !== 'qrScan',
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-2">
                <span className="mr-1 text-sm font-semibold text-gray-700">
                    Workflows:
                </span>
                <button
                    type="button"
                    onClick={() => setSelectedWorkflowId(null)}
                    className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                        selectedWorkflowId === null
                            ? 'border-blue-600 bg-blue-50 font-semibold text-blue-700'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                >
                    <QrCode size={13} /> QR scan
                </button>
                {triggeredWorkflows.map((w) => {
                    const meta = triggerTabMeta(w.triggerType, w.eventName);
                    return (
                        <span key={w.id} className="flex items-center">
                            <button
                                type="button"
                                onClick={() => setSelectedWorkflowId(w.id)}
                                title={meta.title}
                                className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                                    selectedWorkflowId === w.id
                                        ? 'border-blue-600 bg-blue-50 font-semibold text-blue-700'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                {meta.icon} {w.name ?? w.eventName ?? w.triggerType}
                            </button>
                            {selectedWorkflowId === w.id && (
                                <button
                                    type="button"
                                    onClick={() => handleDeleteWorkflow(w)}
                                    title="Delete this workflow"
                                    className="ml-1 rounded-md border border-gray-300 bg-white p-1 text-gray-500 hover:bg-red-50 hover:text-red-600"
                                >
                                    <Trash2 size={12} />
                                </button>
                            )}
                        </span>
                    );
                })}
                <button
                    type="button"
                    onClick={() => handleAddTriggerWorkflow('event')}
                    className="flex items-center gap-1 rounded-md border border-dashed border-gray-400 bg-white px-2 py-1 text-xs text-gray-600 hover:border-blue-400 hover:text-blue-600"
                >
                    <Plus size={13} /> Event
                </button>
                <button
                    type="button"
                    onClick={() => handleAddTriggerWorkflow('share')}
                    className="flex items-center gap-1 rounded-md border border-dashed border-gray-400 bg-white px-2 py-1 text-xs text-gray-600 hover:border-blue-400 hover:text-blue-600"
                >
                    <Plus size={13} /> Share
                </button>
                <button
                    type="button"
                    onClick={() => handleAddTriggerWorkflow('mediaUploaded')}
                    className="flex items-center gap-1 rounded-md border border-dashed border-gray-400 bg-white px-2 py-1 text-xs text-gray-600 hover:border-blue-400 hover:text-blue-600"
                >
                    <Plus size={13} /> Media uploaded
                </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-2">
                <span className="mr-1 text-sm font-semibold text-gray-700">
                    Add step:
                </span>
                {catalog.steps.map((step) => (
                    <button
                        key={step.type}
                        type="button"
                        onClick={() => addNode(step.type)}
                        className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:border-blue-400 hover:text-blue-600"
                    >
                        <Plus size={13} /> {step.label}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={copySelection}
                    title="Copy selected steps (⌘/Ctrl+C)"
                    className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                >
                    <Copy size={13} /> Copy
                </button>
                <button
                    type="button"
                    onClick={pasteFromClipboard}
                    title="Paste steps (⌘/Ctrl+V)"
                    className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                >
                    <ClipboardPaste size={13} /> Paste
                </button>
                <button
                    type="button"
                    onClick={handleExport}
                    title="Export this workflow to a JSON file"
                    className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                >
                    <Download size={13} /> Export
                </button>
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title="Import a workflow from a JSON file"
                    className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                >
                    <FileUp size={13} /> Import
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/json,.json"
                    className="hidden"
                    onChange={handleImportFile}
                />

                <div className="ml-auto flex items-center gap-3">
                    {publishedVersionNumber !== null ? (
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700">
                                v{publishedVersionNumber} live
                            </span>
                            {hasUnpublishedChanges && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">
                                    Unpublished changes
                                </span>
                            )}
                        </span>
                    ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">
                            Not published
                        </span>
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-gray-600">
                        Response
                        <select
                            value={responseType}
                            onChange={(e) => setResponseType(e.target.value)}
                            className="rounded-md border border-gray-300 px-1 py-0.5 text-xs"
                        >
                            {catalog.responses.map((r) => (
                                <option key={r.type} value={r.type}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                        />
                        Enabled
                    </label>
                    <button
                        type="button"
                        onClick={() => setShowRuns(true)}
                        className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                    >
                        <ListChecks size={13} /> Runs
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowHistory(true)}
                        className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                    >
                        <History size={13} /> History
                    </button>
                    <button
                        type="button"
                        onClick={autoLayout}
                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                    >
                        Auto-layout
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-1 rounded-md border border-blue-600 bg-white px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                    >
                        {isSaving ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <Save size={13} />
                        )}
                        Save draft
                    </button>
                    <button
                        type="button"
                        onClick={handlePublish}
                        disabled={isPublishing}
                        className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        {isPublishing ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <UploadCloud size={13} />
                        )}
                        Publish
                    </button>
                </div>
            </div>

            <div className="flex gap-4">
                <div
                    className="relative h-[560px] flex-1 rounded-md border border-gray-200"
                    style={{ minWidth: 0 }}
                >
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={nodeTypes}
                        onNodeClick={(_, node) => setSelectedId(node.id)}
                        onPaneClick={() => setSelectedId(null)}
                        fitView
                    >
                        <Background />
                        <Controls />
                        <Panel position="top-left">
                            <div className="rounded bg-white/80 px-2 py-1 text-[11px] text-gray-500">
                                Drag from a node's right edge to connect steps.
                            </div>
                        </Panel>
                    </ReactFlow>
                </div>

                <div className="w-[340px] shrink-0 rounded-md border border-gray-200 p-3">
                    {selectedNode ? (
                        <NodeConfigPanel
                            node={
                                {
                                    id: selectedNode.id,
                                    type: (selectedNode.data as RFNodeData)
                                        .nodeType,
                                    key: (selectedNode.data as RFNodeData)
                                        .nodeKey,
                                    label: (selectedNode.data as RFNodeData)
                                        .label,
                                    data: (selectedNode.data as RFNodeData)
                                        .config,
                                } as WorkflowNode
                            }
                            suggestions={suggestions}
                            triggerOptions={catalog.triggers}
                            onUpdateMeta={updateSelectedMeta}
                            onUpdateConfig={updateSelectedConfig}
                            onDelete={deleteSelected}
                            onDuplicate={() =>
                                selectedId && duplicateNode(selectedId)
                            }
                        />
                    ) : (
                        <div className="text-sm text-gray-400">
                            Select a step to configure it. Use ⌘/Ctrl+C and
                            ⌘/Ctrl+V to copy and paste steps, or ⌘/Ctrl+D to
                            duplicate the selected step.
                        </div>
                    )}
                </div>
            </div>

            {showHistory && (
                <VersionHistoryPanel
                    activationId={activationId}
                    workflowId={selectedWorkflowId}
                    onClose={() => setShowHistory(false)}
                    onRestored={handleRestored}
                />
            )}

            {showRuns && (
                <RunHistoryPanel
                    activationId={activationId}
                    onClose={() => setShowRuns(false)}
                />
            )}

            <div className="rounded-md border border-gray-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700">
                        Test run
                    </span>
                    <button
                        type="button"
                        onClick={handleTest}
                        disabled={isTesting}
                        className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                        {isTesting ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <Play size={13} />
                        )}
                        Run with sample input
                    </button>
                </div>
                <textarea
                    className="h-28 w-full rounded-md border border-gray-300 p-2 font-mono text-xs"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                />
                {testResult && (
                    <div className="mt-3 space-y-2 text-xs">
                        {testResult.success ? (
                            <>
                                <div className="font-semibold text-emerald-700">
                                    Emitted codes
                                </div>
                                <pre className="overflow-auto rounded bg-gray-900 p-2 text-emerald-200">
                                    {JSON.stringify(testResult.output, null, 2)}
                                </pre>
                                <div className="font-semibold text-gray-700">
                                    Step trace
                                </div>
                                <pre className="max-h-48 overflow-auto rounded bg-gray-100 p-2 text-gray-700">
                                    {JSON.stringify(testResult.trace, null, 2)}
                                </pre>
                            </>
                        ) : (
                            <div className="rounded bg-red-50 p-2 text-red-700">
                                {testResult.error}
                                {testResult.details && (
                                    <ul className="ml-4 list-disc">
                                        {testResult.details.map((d, i) => (
                                            <li key={i}>{d}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function WorkflowBuilder({
    activationId,
}: WorkflowBuilderProps) {
    return (
        <ReactFlowProvider>
            <Inner activationId={activationId} />
        </ReactFlowProvider>
    );
}
